// Phase 1a Polish — Per-dynamic-type list view with spreadsheet-style inline editing.
//
// Route: /assets/type/:typeKey (e.g. /assets/type/smart_charger)
// Layout: header (type label + count + back + New) → search + filter strip → table.
// Name column is a Link to the detail page (operator can drill for full edit).
// Other columns are inline-editable via InlineEditField (click cell → input → blur/Enter saves).
// SmartCharger gets the richest column set (power, VPN, build phase, needs update, customer).
// TimeMachine mirrors SmartCharger minus power/needs_update/customer.
// RTK + GroundStationKit render a simpler table — not much to mass-edit on those yet.
// Every other type — including one an admin mints at /admin/asset-types — falls through
// to the same generic table, so a brand-new type lists its assets the day it is created.
//
// Sort: the SmartCharger sheet's headers toggle the page's own sort state (it is the
// donor of the cell dialect and migrates onto the shared table last). Every other table
// here IS the shared table and owns its own sort. Both are client-side over loaded rows.
// Search: filters by display_code, notes, vpn_ip, firmware — cheap local includes() scan.

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { buttonClass } from '../components/ui/Button';
import { Link, useParams } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useApi } from "../apiHelpers";
import BackLink from "../components/BackLink";
import { DataTable } from "../components/table/DataTable";
import { useToast } from "../components/Toast/ToastContext";
import useAssetsStore from "../stores/useAssetsStore";
import useUserStore from "../stores/useUserStore";
import {
	Asset,
	AssetType,
	AssetExtension,
	BuildPhase,
	SmartChargerDetails,
	TimeMachineDetails,
	BUILD_PHASE_LABELS,
	BUILD_PHASE_ORDER,
	CHARGER_BUILD_PHASE_LABELS,
	CHARGER_BUILD_PHASE_ORDER,
	chargerStatus,
	CHARGER_STATUS_LABELS,
	AssetOwnership,
	OWNERSHIP_LABELS,
	ownershipSortValue,
} from "./types";
import InlineEditField from "./InlineEditField";
import "./AssetsTypeList.css";
import { useSheetSelection, SheetApi, SheetCol } from "./useSheetSelection";
import NotesCell from "./NotesCell";
import PageLayout from "../components/ui/PageLayout";
import Chip, { ChipGroup } from "../components/ui/Chip";
import SearchField from "../components/ui/SearchField";
import Tag from "../components/ui/Tag";
import Tooltip from "../components/ui/Tooltip";
import { useConfirm } from "../components/ConfirmModal/ConfirmContext";
import EmptyState from "../components/ui/EmptyState";
import { Skeleton, SkeletonGroup } from "../components/ui/Skeleton";
import { AssetStatusTag, ChargerStatusTag } from "./StatusTags";

// Embedded in another page (Charge Control's Asset tab) the host owns the gutter.
const TypeListShell: React.FC<{ embedded?: boolean; children: React.ReactNode }> = ({ embedded, children }) =>
	embedded ? (
		<div className="space-y-4">{children}</div>
	) : (
		<PageLayout kind="workspace" className="pt-4 space-y-4">
			{children}
		</PageLayout>
	);

// TimeMachine build-phase dropdown options (imaging phases).
const BUILD_PHASE_OPTIONS = BUILD_PHASE_ORDER.map((v) => ({
	value: v,
	label: BUILD_PHASE_LABELS[v],
}));
// SmartCharger build-phase dropdown options (8-step physical pipeline).
const CHARGER_BUILD_PHASE_OPTIONS = CHARGER_BUILD_PHASE_ORDER.map((v) => ({
	value: v,
	label: CHARGER_BUILD_PHASE_LABELS[v],
}));
// Type = charger wattage. Labels per operator convention: regular → 600w.
const POWER_OPTIONS = [
	{ value: "regular", label: "600w" },
	{ value: "super", label: "Super" },
];

// Live telemetry for a charger, passed in by the /charge > Manage host (from the
// collector fleet). Keyed by asset_id. Absent on the standalone /assets page.
export interface ChargerLiveEntry {
	online: boolean;
	last_seen: string | null;
	geo_label: string | null;
}

// Relative "time ago" for a charger's last successful poll (collector ISO stamp).
// Local copy so the Assets domain doesn't import from ChargeControl.
function fmtLastSeen(iso: string | null): string | null {
	if (!iso) return null;
	const t = new Date(iso).getTime();
	if (!isFinite(t)) return null;
	const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
	if (secs < 45) return "just now";
	const mins = Math.round(secs / 60);
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.round(mins / 60);
	if (hrs < 24) return `${hrs}h ago`;
	return `${Math.round(hrs / 24)}d ago`;
}

// SmartCharger spreadsheet columns — ORDER MUST MATCH SmartChargerTable's
// header/cells (used by the selection engine's cellIndex mapping + fill/paste).
// The read-only "live" column is spliced in after Status when live data is passed.
const SC_SHEET_COLS: SheetCol[] = [
	{ key: "customer", editable: true },
	{ key: "display_code", editable: false },
	{ key: "charger_power", editable: true },
	{ key: "build_phase", editable: true },
	{ key: "operational_status", editable: false },
	{ key: "ownership", editable: false },
	{ key: "vpn_ip", editable: true },
	{ key: "vpn_confirmed", editable: true },
	{ key: "on_latest_firmware", editable: true },
	{ key: "notes", editable: true },
];

// Asset types that carry a vpn_ip (smart_charger + time_machine via their extension
// tables, ground_station_kit via gcs_details). These default to sorting by VPN IP;
// everything else (rtk_unit, stable gear) defaults to Name. The set is fixed in code
// because VPN columns are tied to hand-built extension tables, not the dynamic registry.
const VPN_TYPE_KEYS = new Set(["smart_charger", "time_machine", "ground_station_kit"]);

// Type keys with a hand-built table below. Anything not listed renders the generic
// SimpleTable instead. Membership is an override list, never a gate: a type missing
// from it still lists, it just lists with spine columns only.
const BESPOKE_TABLE_KEYS = new Set([
	"smart_charger",
	"time_machine",
	"rtk_unit",
	"ground_station_kit",
]);

const defaultSortCol = (key: string | undefined): string =>
	key === "smart_charger" ? "customer"
		: key && VPN_TYPE_KEYS.has(key) ? "vpn_ip"
		: "display_code";

// Strip the redundant "SmartCharger" prefix for display — every row is a
// SmartCharger, so the bare 4-char code is what operators actually search by.
// Display-only: the stored display_code (barcode/VPN/sequence-linked) is untouched.
const chargerShortName = (displayCode: string): string =>
	displayCode.replace(/^smartcharger/i, "") || displayCode;

// VPN IPs are IPv4 dotted quads (e.g. "10.8.5.20"). A plain string sort orders ".10"
// ahead of ".2" because it compares character-by-character. Fold the quad into a single
// 32-bit integer so it sorts numerically. Blank/malformed IPs return Infinity so they
// sink to the bottom in ascending order (VPN-less rows last).
const vpnSortKey = (ip: unknown): number => {
	const octets = String(ip ?? "").trim().split(".");
	if (octets.length !== 4) return Number.POSITIVE_INFINITY;
	let key = 0;
	for (const o of octets) {
		const n = Number(o);
		if (!Number.isInteger(n) || n < 0 || n > 255) return Number.POSITIVE_INFINITY;
		key = key * 256 + n;
	}
	return key;
};

// The accessorFn spelling of the same key. The shared table's contract wants
// `undefined` for an empty rather than a sentinel, and it lands on exactly the
// order the Infinity gave us: VPN-less rows last ascending, first descending.
const vpnSortValue = (ip: unknown): number | undefined => {
	const key = vpnSortKey(ip);
	return Number.isFinite(key) ? key : undefined;
};

// Per-type list rows include the extension shape inlined into the asset object.
// For kits, the backend list query supplies _children_count and _flagged_children_count
// so a kit row can show its component total and a "has flagged items" indicator without
// per-row fan-out. The list response carries no `children` array — only GET /assets/:id does.
interface AssetWithExt extends Asset {
	_ext?: AssetExtension | null;
	_children_count?: number;
	_flagged_children_count?: number;
}

// A column that lives on only SOME extension shapes, read the way the cells have
// always read it: `_ext` is a union, so it goes through the loose record cast
// rather than a per-type narrow.
const extString = (r: AssetWithExt, key: string): string | null => {
	const ext = r._ext as unknown as Record<string, unknown> | undefined;
	const v = ext?.[key];
	return v == null ? null : String(v);
};

// Ownership: whose is it? Derived server-side and served as `ownership` on every row.
// Status already owns the pill on these tables, so ownership stays quiet text — and a
// null renders as the house empty-value placeholder, because an unaudited row is an
// absence of an answer, not a fault. Module scope, so flexRender keeps the cell mounted.
const OwnershipCell: React.FC<{
	ownership: AssetOwnership | null | undefined;
}> = ({ ownership }) =>
	ownership ? (
		<span className="text-on-surface">{OWNERSHIP_LABELS[ownership]}</span>
	) : (
		<span className="text-on-surface-disabled italic">—</span>
	);

interface CustomerLite {
	id: number;
	name: string;
}

type SortState = { col: string; dir: "asc" | "desc" };

// Route component by default (typeKey from the URL). Also embeddable — pass
// `typeKey` + `embedded` to host it inside another page (e.g. Charge Control's
// Asset tab), which drops the back-link and the standalone page container.
const AssetsTypeList: React.FC<{
	typeKey?: string;
	embedded?: boolean;
	liveByAssetId?: Record<string, ChargerLiveEntry>;
	onOpenLive?: (assetId: string) => void;
}> = ({ typeKey: propTypeKey, embedded, liveByAssetId, onOpenLive }) => {
	const { typeKey: paramTypeKey } = useParams<{ typeKey: string }>();
	const typeKey = propTypeKey ?? paramTypeKey;
	const api = useApi();
	const toast = useToast();
	const confirm = useConfirm();
	const { types, ensureTypesLoaded } = useAssetsStore();
	const user = useUserStore((s: any) => s.user);
	const isAdmin = !!user?.can_admin;

	const [rows, setRows] = useState<AssetWithExt[]>([]);
	const [customers, setCustomers] = useState<CustomerLite[]>([]);
	const [loading, setLoading] = useState(true);
	const [includeRetired, setIncludeRetired] = useState(false);
	const [search, setSearch] = useState("");
	const [phaseFilters, setPhaseFilters] = useState<string[]>([]);
	const [sort, setSort] = useState<SortState>(() => ({
		col: defaultSortCol(typeKey),
		dir: "asc",
	}));
	// Host for the SmartCharger sheet — used to measure the frozen Customer column
	// so the frozen Name column can offset by its real width.
	const sheetRef = useRef<HTMLDivElement>(null);
	// Caller's pinned charger asset_ids ("My Chargers") — pinned rows float to the top.
	const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());
	const [wrongVpnIds, setWrongVpnIds] = useState<Set<string>>(new Set());

	// This route component is reused (not remounted) when navigating between asset
	// types, so the lazy useState default above only fires once. Reset the sort to the
	// new type's default on an actual type switch — but never on plain re-renders, so a
	// manual sort within a list survives. The ref guard skips the redundant mount call.
	const prevTypeKey = useRef(typeKey);
	useEffect(() => {
		if (prevTypeKey.current !== typeKey) {
			prevTypeKey.current = typeKey;
			setSort({ col: defaultSortCol(typeKey), dir: "asc" });
			// Another type's assets must not sit dimmed under this type's title while it loads.
			setRows([]);
		}
	}, [typeKey]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		ensureTypesLoaded().catch(() =>
			toast.error("Failed to load asset types"),
		);
	}, []);

	// Fetch customer list once — used for the Customer inline-select column on SmartCharger.
	// /orders/customers returns {id, name} shape per SalesOrderForm.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		api.get("/orders/customers")
			.then((data: CustomerLite[]) =>
				setCustomers(Array.isArray(data) ? data : []),
			)
			.catch(() => setCustomers([]));
	}, []);

	const type: AssetType | undefined = useMemo(
		() => types.find((t) => t.key === typeKey),
		[types, typeKey],
	);

	const fetchRows = useCallback(async () => {
		if (!type) return;
		setLoading(true);
		try {
			const params = new URLSearchParams();
			params.set("asset_type_id", String(type.id));
			params.set("is_internal_pool", "all");
			// A sold unit is still a unit of this type, and hiding it made the list
			// disagree with the landing card's total. Both ownership axes come back
			// unfiltered; the Ownership column is what separates them on screen.
			params.set("is_sold", "all");
			params.set("is_active", includeRetired ? "all" : "true");
			params.set("pageSize", "200");

			const listResp = await api.get(`/assets?${params.toString()}`);
			const list: Array<
				Asset & {
					_extension?: AssetExtension | null;
					_children_count?: number;
					_flagged_children_count?: number;
				}
			> = listResp.data;

			// Backend folds the extension table into the list query when filtering by a single
			// asset_type_id with an extension table — no per-row /assets/:id fan-out needed.
			// Kit types additionally get the child counts behind the Components column.
			const withExt: AssetWithExt[] = list.map((a) => ({
				...a,
				_ext: a._extension ?? null,
				_children_count: a._children_count ?? 0,
				_flagged_children_count: a._flagged_children_count ?? 0,
			}));
			setRows(withExt);
		} catch {
			toast.error("Failed to load list");
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [type?.id, includeRetired]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		fetchRows();
	}, [fetchRows]);

	// Frozen Name column (2nd) must start exactly where the frozen Customer column
	// (1st) ends. Customer width is content-driven, so measure it and publish it as
	// --sc-col1; a ResizeObserver keeps it correct as data/layout change.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (type?.key !== "smart_charger") return;
		const host = sheetRef.current;
		if (!host || typeof ResizeObserver === "undefined") return;
		const th = host.querySelector<HTMLElement>("thead th:first-child");
		if (!th) return;
		const apply = () => host.style.setProperty("--sc-col1", `${th.offsetWidth}px`);
		apply();
		const ro = new ResizeObserver(apply);
		ro.observe(th);
		return () => ro.disconnect();
	}, [type?.key, rows.length]);

	// SmartCharger only: the caller's pinned charger asset_ids, so "My Chargers"
	// float to the top of the Manage table (matches the Control view's pins).
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (type?.key !== "smart_charger") {
			setPinnedIds(new Set());
			return;
		}
		let cancelled = false;
		api.get("/charge/charger-pins")
			.then((d: { asset_ids?: string[] }) => {
				if (!cancelled) setPinnedIds(new Set(d?.asset_ids ?? []));
			})
			.catch(() => {
				if (!cancelled) setPinnedIds(new Set());
			});
		return () => {
			cancelled = true;
		};
	}, [type?.key]);

	// SmartCharger only: asset_ids involved in a VPN-profile inconsistency, for the
	// per-row warning glyph. Same fetch shape as the pins effect above.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (type?.key !== "smart_charger") {
			setWrongVpnIds(new Set());
			return;
		}
		let cancelled = false;
		api.get("/charge/inconsistencies")
			.then((r: { wrong_profile?: any[]; vpn_mismatch?: any[] }) => {
				if (cancelled) return;
				const ids = new Set<string>();
				(r?.wrong_profile ?? []).forEach((w) => {
					if (w?.true_asset?.asset_id) ids.add(w.true_asset.asset_id);
					if (w?.profile_asset?.asset_id) ids.add(w.profile_asset.asset_id);
				});
				(r?.vpn_mismatch ?? []).forEach((v) => {
					if (v?.asset_id) ids.add(v.asset_id);
				});
				setWrongVpnIds(ids);
			})
			.catch(() => {
				if (!cancelled) setWrongVpnIds(new Set());
			});
		return () => {
			cancelled = true;
		};
	}, [type?.key]);

	const saveExtensionField =
		(assetId: string) => (fieldKey: string) => async (v: unknown) => {
			try {
				const updated: Asset = await api.patch(`/assets/${assetId}`, {
					body: { extension: { [fieldKey]: v } },
				});
				setRows((prev) =>
					prev.map((r) =>
						r.id === assetId
							? {
									...r,
									...updated,
									_ext: updated.extension ?? null,
								}
							: r,
					),
				);
			} catch (err) {
				toast.error(`Couldn't save ${fieldKey.replace(/_/g, " ")}`);
				throw err;
			}
		};

	const saveSpineField =
		(assetId: string) => (fieldKey: string) => async (v: unknown) => {
			try {
				const updated: Asset = await api.patch(`/assets/${assetId}`, {
					body: { [fieldKey]: v },
				});
				setRows((prev) =>
					prev.map((r) =>
						r.id === assetId
							? {
									...r,
									...updated,
									_ext: updated.extension ?? null,
								}
							: r,
					),
				);
			} catch (err) {
				toast.error(`Couldn't save ${fieldKey.replace(/_/g, " ")}`);
				throw err;
			}
		};

	// Reassigning a charger to a customer must also clear any current_event_id / current_tour_id
	// to respect the assets_commit_mutex_check constraint. Only clear those when SETTING a customer —
	// detaching a customer (clear → null) must not also wipe an event/tour commit the operator
	// didn't ask to touch.
	const saveCustomer = (assetId: string) => async (customerId: unknown) => {
		try {
			const isClearing = customerId === "" || customerId === null;
			const body: Record<string, unknown> = isClearing
				? { current_customer_id: null }
				: {
					current_customer_id: Number(customerId),
					current_event_id: null,
					current_tour_id: null,
				};
			const updated: Asset = await api.patch(`/assets/${assetId}`, {
				body,
			});
			setRows((prev) =>
				prev.map((r) =>
					r.id === assetId
						? { ...r, ...updated, _ext: updated.extension ?? null }
						: r,
				),
			);
		} catch (err) {
			toast.error(`Couldn't reassign customer`);
			throw err;
		}
	};

	// Admin-only fleetwide flip — flags every SmartCharger as needing a firmware update.
	const handleLogFirmwareUpdate = async () => {
		if (
			!(await confirm({
				message: `Flag all ${rows.length} SmartChargers as needing firmware update?`,
			}))
		)
			return;
		try {
			const result = await api.post(
				"/assets/smart-chargers/mark-all-needs-update",
				{ body: { confirm: true } },
			);
			toast.success(
				`Flagged ${result.flagged} chargers as needing update`,
			);
			await fetchRows();
		} catch {
			toast.error("Failed to update firmware status");
		}
	};

	const toggleSort = (col: string) => {
		setSort((s) =>
			s.col === col
				? { col, dir: s.dir === "asc" ? "desc" : "asc" }
				: { col, dir: "asc" },
		);
	};

	// Whether this type carries a build_phase column (smart_charger + time_machine).
	// Drives whether the build-phase chip strip renders.
	const hasBuildPhase = type?.key === "smart_charger" || type?.key === "time_machine";

	// SmartCharger and TimeMachine now carry DIFFERENT build-phase vocabularies
	// (charger = 8 physical phases; TM = 5 imaging phases). Pick the active type's.
	const isCharger = type?.key === "smart_charger";
	const phaseOrder: string[] = isCharger ? CHARGER_BUILD_PHASE_ORDER : BUILD_PHASE_ORDER;
	const phaseLabels: Record<string, string> = isCharger
		? CHARGER_BUILD_PHASE_LABELS
		: BUILD_PHASE_LABELS;

	// Apply search + phase filter + sort. All lightweight enough for the ~200-row Phase 1a ceiling.
	const visible = useMemo(() => {
		const q = search.trim().toLowerCase();
		const searched =
			q === ""
				? rows
				: rows.filter((r) => {
						const ext = r._ext as unknown as
							| Record<string, unknown>
							| undefined;
						const haystack = [
							r.display_code,
							r.notes ?? "",
							r.barcode ?? "",
							String(ext?.vpn_ip ?? ""),
							String(ext?.firmware_version ?? ""),
						]
							.join(" ")
							.toLowerCase();
						return haystack.includes(q);
					});

		// Phase filter: empty array = "All" (no filter). Otherwise keep rows whose
		// build_phase is in the selected set. No-op for types without build_phase.
		const filtered =
			!hasBuildPhase || phaseFilters.length === 0
				? searched
				: searched.filter((r) => {
						const ext = r._ext as unknown as Record<string, unknown> | undefined;
						return phaseFilters.includes(String(ext?.build_phase ?? ""));
					});

		// Only SmartChargerTable's headers still feed `sort`, so the reachable
		// columns are its nine plus whatever defaultSortCol can return. The
		// other tables sort themselves inside the shared table.
		const getter = (r: AssetWithExt) => {
			const ext = r._ext as unknown as
				| Record<string, unknown>
				| undefined;
			switch (sort.col) {
				case "display_code":
					return r.display_code.toLowerCase();
				case "sequence_number":
					return Number(ext?.sequence_number ?? 0);
				case "charger_power":
					return String(ext?.charger_power ?? "");
				case "vpn_ip":
					return vpnSortKey(ext?.vpn_ip);
				case "vpn_confirmed":
					return ext?.vpn_confirmed ? 1 : 0;
				case "build_phase":
					return String(ext?.build_phase ?? "");
				case "on_latest_firmware":
					return ext?.on_latest_firmware ? 1 : 0;
				case "operational_status":
					// SmartCharger's Status column shows the derived charger_status,
					// so sort by that label; other types sort by raw operational_status.
					return type?.key === "smart_charger"
						? CHARGER_STATUS_LABELS[chargerStatus(r)]
						: r.operational_status;
				case "ownership":
					// Lifecycle rank, with the unclassified rows as one block at the end
					// (Number.MAX_SAFE_INTEGER rather than 0, so the audit worklist groups
					// after the answered rows ascending instead of leading the table).
					return ownershipSortValue(r.ownership) ?? Number.MAX_SAFE_INTEGER;
				case "notes":
					return (r.notes ?? "").toLowerCase();
				case "customer":
					return r.current_customer_id ?? 0;
				default:
					return r.display_code.toLowerCase();
			}
		};

		const sorted = [...filtered].sort((a, b) => {
			// Pinned ("My Chargers") float to the top, then the active sort within each group.
			const ap = pinnedIds.has(a.id) ? 0 : 1;
			const bp = pinnedIds.has(b.id) ? 0 : 1;
			if (ap !== bp) return ap - bp;
			const av = getter(a) as string | number;
			const bv = getter(b) as string | number;
			if (av < bv) return sort.dir === "asc" ? -1 : 1;
			if (av > bv) return sort.dir === "asc" ? 1 : -1;
			return 0;
		});
		return sorted;
	}, [rows, search, sort, phaseFilters, hasBuildPhase, pinnedIds]);

	// --- SmartCharger spreadsheet selection engine ---------------------------
	// Live "Last seen · Location" column appears only when the /charge > Manage
	// host passes live telemetry. It's read-only; splicing it into the column
	// model keeps the engine's cellIndex mapping aligned with the rendered cells.
	const showLive = type?.key === "smart_charger" && !!liveByAssetId;
	const scCols = useMemo<SheetCol[]>(() => {
		if (!showLive) return SC_SHEET_COLS;
		const c = [...SC_SHEET_COLS];
		c.splice(5, 0, { key: "live", editable: false }); // after Status, before Ownership
		return c;
	}, [showLive]);
	// Read/write a cell by (row index into `visible`, col index into scCols).
	// The hook captures the latest closures each render, so plain consts are fine.
	const scReadCell = (r: number, c: number): string => {
		const row = visible[r];
		if (!row) return "";
		const ext = row._ext as unknown as Record<string, unknown> | undefined;
		switch (scCols[c].key) {
			case "customer":
				return row.current_customer_id == null ? "" : String(row.current_customer_id);
			case "display_code":
				return row.display_code;
			case "operational_status":
				return row.operational_status;
			case "ownership":
				// Read-only but worth copying out: the label, and an empty string for the
				// unclassified rows so a pasted block carries the blank as a blank.
				return row.ownership ? OWNERSHIP_LABELS[row.ownership] : "";
			case "notes":
				return row.notes ?? "";
			case "live":
				return ""; // read-only, not copyable
			case "vpn_confirmed":
				return ext?.vpn_confirmed ? "true" : "false";
			case "on_latest_firmware":
				return ext?.on_latest_firmware ? "true" : "false";
			default: {
				const v = ext?.[scCols[c].key];
				return v == null ? "" : String(v);
			}
		}
	};
	const scWriteCell = async (r: number, c: number, value: string): Promise<void> => {
		const row = visible[r];
		if (!row) return;
		if (!scCols[c].editable) return; // read-only cols (live/name/status) are never written
		const key = scCols[c].key;
		if (key === "customer") { await saveCustomer(row.id)(value === "" ? null : value); return; }
		if (key === "notes") { await saveSpineField(row.id)("notes")(value === "" ? null : value); return; }
		if (key === "vpn_confirmed" || key === "on_latest_firmware") {
			await saveExtensionField(row.id)(key)(value === "true");
			return;
		}
		await saveExtensionField(row.id)(key)(value === "" ? null : value);
	};
	const scSheet = useSheetSelection({
		rowCount: type?.key === "smart_charger" ? visible.length : 0,
		cols: scCols,
		readCell: scReadCell,
		writeCell: scWriteCell,
		containerRef: sheetRef,
	});

	if (!type && types.length > 0) {
		return (
			<div className="p-8 text-center text-on-surface-variant">
				Unknown asset type: {typeKey}
			</div>
		);
	}

	return (
		<TypeListShell embedded={embedded}>
			<div className="flex justify-between items-center">
				<div>
					{!embedded && (
						<BackLink to="/assets" label="Assets" className="mb-2" />
					)}
					<h1 className="text-2xl font-bold text-primary">
						{type?.label ?? "…"}
						{visible.length > 0 && (
							<span className="text-on-surface-variant text-base font-normal ml-2">
								({visible.length})
							</span>
						)}
					</h1>
				</div>
				<div className="flex gap-2 items-center">
					{type?.key === "smart_charger" && isAdmin && (
						<Tooltip label="Mark every SmartCharger as needing a firmware update">
							<button
								onClick={handleLogFirmwareUpdate}
								className={buttonClass('tonal', 'md')}
							>
								Mark Firmware Update
							</button>
						</Tooltip>
					)}
					<Link
						to={
							type
								? `/assets/new?type=${encodeURIComponent(type.key)}&category=${encodeURIComponent(type.category)}`
								: "/assets/new"
						}
						className={buttonClass('filled', 'md')}
					>
						+ New {type?.label ?? "Asset"}
					</Link>
				</div>
			</div>

			<div className="space-y-2.5">
				<div className="flex gap-3 items-center flex-wrap">
					<SearchField
						value={search}
						onChange={setSearch}
						label={`Search ${type?.label ?? "assets"}`}
						placeholder={`Search ${type?.label ?? "assets"}…`}
						className="flex-1 min-w-[240px]"
					/>
					<Chip
						pressed={includeRetired}
						onClick={() => setIncludeRetired((v) => !v)}
					>
						Include retired
					</Chip>
				</div>

				{/* Build-phase filter chips — only relevant for types that carry a build_phase
					column (smart_charger, time_machine). Multi-select; empty = "All". Mirrors
					the OrdersPage chip pattern for visual consistency. */}
				{hasBuildPhase && (
					<ChipGroup label="Build phase">
						{["", ...phaseOrder].map((p) => {
							const isAllChip = p === "";
							const isActive = isAllChip
								? phaseFilters.length === 0
								: phaseFilters.includes(p);
							const count = isAllChip
								? rows.length
								: rows.filter((r) => {
										const ext = r._ext as unknown as Record<string, unknown> | undefined;
										return ext?.build_phase === p;
									}).length;
							const onClick = () => {
								if (isAllChip) {
									setPhaseFilters([]);
								} else {
									setPhaseFilters((prev) =>
										prev.includes(p)
											? prev.filter((x) => x !== p)
											: [...prev, p],
									);
								}
							};
							return (
								<Chip
									key={p || "all"}
									pressed={isActive}
									count={count}
									onClick={onClick}
								>
									{isAllChip ? "All" : phaseLabels[p]}
								</Chip>
							);
						})}
					</ChipGroup>
				)}
			</div>

			{type?.key === "smart_charger" && scSheet.selectedCount > 0 && (
				<div className="flex items-center gap-2 flex-wrap text-xs bg-gray-900/60 border border-gray-700/60 rounded-md px-3 py-2">
					<span className="text-on-surface font-medium">
						{scSheet.selectedCount} cell{scSheet.selectedCount === 1 ? "" : "s"} selected
					</span>
					<button
						type="button"
						onClick={scSheet.fillDown}
						disabled={scSheet.busy}
						className={buttonClass('quiet', 'sm')}
					>
						Fill down
					</button>
					<button
						type="button"
						onClick={scSheet.copy}
						disabled={scSheet.busy}
						className={buttonClass('quiet', 'sm')}
					>
						Copy
					</button>
					<button
						type="button"
						onClick={scSheet.paste}
						disabled={scSheet.busy || !scSheet.hasClipboard}
						className={buttonClass('quiet', 'sm')}
					>
						Paste
					</button>
					{scSheet.busy && <span className="text-on-surface-variant">saving…</span>}
					<span className="ml-auto text-on-surface-variant hidden sm:inline">
						arrows move · shift+arrows select · ⌘/Ctrl+D fill · C copy · V paste · Esc clear
					</span>
				</div>
			)}

			{/* The sheet's own vessel, and only the sheet's: the box, the accent
				hairline and the focusable scrollport are the cell dialect's
				furniture (sheetRef measures the frozen first column, .sc-sheet
				carries the gridlines and the sticky/frozen planes, tabIndex is
				where the arrow keys land). Every other table below is the shared
				DataTable, which draws its own shell — wrapping one in this box
				would put a second vessel around the real one. */}
			{type?.key === "smart_charger" && (
				<div
					className="bg-surface-container-low rounded-md overflow-hidden relative"
					style={{
						boxShadow:
							"0 0 40px rgba(255,204,51,0.04), 0 1px 3px rgba(0,0,0,.4)",
					}}
				>
					<div
						className="absolute top-0 left-0 right-0 h-[2px] rounded-t-md"
						style={{
							background:
								"linear-gradient(90deg, rgba(255,204,51,0.4) 0%, rgba(255,204,51,0.1) 50%, transparent 100%)",
						}}
					/>

					<div
						ref={sheetRef}
						className="overflow-x-auto focus:outline-none sc-sheet"
						tabIndex={0}
						onKeyDown={scSheet.onKeyDown}
					>
						<SmartChargerTable
							rows={visible}
							loading={loading}
							sort={sort}
							onSort={toggleSort}
							customers={customers}
							saveExtensionField={saveExtensionField}
							saveSpineField={saveSpineField}
							saveCustomer={saveCustomer}
							sheet={scSheet}
							pinnedIds={pinnedIds}
							wrongVpnIds={wrongVpnIds}
							showLive={showLive}
							liveByAssetId={liveByAssetId}
							onOpenLive={onOpenLive}
						/>
					</div>
				</div>
			)}
			{type?.key === "time_machine" && (
				<TimeMachineTable
					rows={visible}
					loading={loading}
					saveExtensionField={saveExtensionField}
					saveSpineField={saveSpineField}
				/>
			)}
			{type?.key === "rtk_unit" && (
				<SimpleTable
					rows={visible}
					loading={loading}
					saveSpineField={saveSpineField}
					columns={["firmware", "customer"]}
					type={type}
					customers={customers}
					saveCustomer={saveCustomer}
				/>
			)}
			{type?.key === "ground_station_kit" && (
				<SimpleTable
					rows={visible}
					loading={loading}
					saveSpineField={saveSpineField}
					columns={["vpn_ip", "components", "customer"]}
					type={type}
					customers={customers}
					saveCustomer={saveCustomer}
				/>
			)}
			{/* Every type without a hand-built table above. Admin-created types carry
				no extension table and no custom fields (AssetTypesAdmin pins
				requires_extension_table to false), so the spine is all there is to
				show — plus the component count when the type is a kit. Types that DO
				have an extension table land here too if nothing links to them: /assets/type/vehicle
				is reachable by typing it, and renders the spine while Dispatch stays
				the surface vehicles are actually worked from. */}
			{type && !BESPOKE_TABLE_KEYS.has(type.key) && (
				<SimpleTable
					rows={visible}
					loading={loading}
					saveSpineField={saveSpineField}
					columns={
						type.category === "kit"
							? ["components", "customer", "location"]
							: ["customer", "location"]
					}
					type={type}
					customers={customers}
					saveCustomer={saveCustomer}
				/>
			)}
		</TypeListShell>
	);
};

// --- Table components -------------------------------------------------------

// The Ownership column, shared by every shared-table surface here. Sorted by the
// lifecycle rank rather than the label, with `undefined` for the unclassified rows —
// the table's contract for an empty, and what groups the audit worklist at the end.
// `csv` overrides that rank so an export carries the words, not 0/1/2.
const OWNERSHIP_COLUMN: ColumnDef<AssetWithExt, any> = {
	id: "ownership",
	header: "Ownership",
	accessorFn: (r) => ownershipSortValue(r.ownership),
	meta: {
		csv: (r: AssetWithExt) =>
			r.ownership ? OWNERSHIP_LABELS[r.ownership] : "",
	},
	cell: ({ row }) => <OwnershipCell ownership={row.original.ownership} />,
};

type SaveFactory = (
	assetId: string,
) => (fieldKey: string) => (v: unknown) => Promise<void>;
type SaveCustomerFactory = (
	assetId: string,
) => (customerId: unknown) => Promise<void>;

// Page-owned sort, now down to ONE consumer: SmartChargerTable. It is the donor
// of the sheet cell dialect and converges onto the shared table last, so its
// headers still call back into the page's SortState. The other two tables hold
// their sort inside the shared table.
interface CommonTableProps {
	loading: boolean;
	sort: SortState;
	onSort: (col: string) => void;
}

// SmartCharger — Columns:
//   Customer | Name | Type | Build Phase | Status | Ownership | VPN | VPN✓ | Latest App | Notes
// Customer and Ownership answer different questions: who holds it, and whose it is.
const SmartChargerTable: React.FC<
	CommonTableProps & {
		rows: AssetWithExt[];
		customers: { id: number; name: string }[];
		saveExtensionField: SaveFactory;
		saveSpineField: SaveFactory;
		saveCustomer: SaveCustomerFactory;
		sheet: SheetApi;
		pinnedIds: Set<string>;
		wrongVpnIds: Set<string>;
		showLive: boolean;
		liveByAssetId?: Record<string, ChargerLiveEntry>;
		onOpenLive?: (assetId: string) => void;
	}
> = ({
	rows,
	loading,
	sort,
	onSort,
	customers,
	saveExtensionField,
	saveSpineField,
	saveCustomer,
	sheet,
	pinnedIds,
	wrongVpnIds,
	showLive,
	liveByAssetId,
	onOpenLive,
}) => {
	const customerOptions = useMemo(
		() => customers.map((c) => ({ value: String(c.id), label: c.name })),
		[customers],
	);
	// Selection highlight: one generated CSS rectangle (rows r0..r1 × cols c0..c1)
	// + a ring on the active cell — avoids threading selection props through every
	// cell. nth-child is 1-based.
	const rg = sheet.range;
	const ac = sheet.active;
	const selCss = [
		rg &&
			`.sc-sheet tbody tr:nth-child(n+${rg.r0 + 1}):nth-child(-n+${rg.r1 + 1}) ` +
				`td:nth-child(n+${rg.c0 + 1}):nth-child(-n+${rg.c1 + 1}){background:rgba(255,204,51,0.12);}`,
		ac &&
			`.sc-sheet tbody tr:nth-child(${ac.r + 1}) td:nth-child(${ac.c + 1})` +
				`{box-shadow:inset 0 0 0 2px #ffcc33;}`,
	]
		.filter(Boolean)
		.join("\n");
	// Delegated cell selection: derive (row, col) from the clicked cell's position
	// (sectionRowIndex within tbody, cellIndex within row) — no per-cell wiring.
	const onCellMouseDown = (e: React.MouseEvent) => {
		const el = e.target as HTMLElement;
		const td = el.closest("td");
		if (!td) return;
		const tr = td.parentElement as HTMLTableRowElement | null;
		if (!tr || tr.sectionRowIndex < 0) return;
		// If the click lands on an editor control, select the cell but let the
		// editor keep focus (don't yank it back to the grid via focusGrid).
		const interactive = !!el.closest("button, select, input, textarea, a");
		sheet.selectCell(tr.sectionRowIndex, td.cellIndex, e.shiftKey, !interactive);
	};
	return (
		<>
			{selCss && <style>{selCss}</style>}
			<table className="w-full text-sm" onMouseDown={onCellMouseDown}>
			<thead>
				<tr className="text-[11px] text-on-surface-variant uppercase tracking-wider border-b border-gray-700/60">
					<SortTh
						col="customer"
						label="Customer"
						sort={sort}
						onSort={onSort}
					/>
					<SortTh
						col="display_code"
						label="Name"
						sort={sort}
						onSort={onSort}
					/>
					<SortTh
						col="charger_power"
						label="Type"
						sort={sort}
						onSort={onSort}
					/>
					<SortTh
						col="build_phase"
						label="Build Phase"
						sort={sort}
						onSort={onSort}
					/>
					<SortTh
						col="operational_status"
						label="Status"
						sort={sort}
						onSort={onSort}
						center
					/>
					{showLive && (
						<th className="text-left px-3 py-3 font-semibold whitespace-nowrap">
							Last seen
						</th>
					)}
					<SortTh
						col="ownership"
						label="Ownership"
						sort={sort}
						onSort={onSort}
					/>
					<SortTh
						col="vpn_ip"
						label="VPN IP"
						sort={sort}
						onSort={onSort}
					/>
					<SortTh
						col="vpn_confirmed"
						label="VPN ✓"
						sort={sort}
						onSort={onSort}
						center
					/>
					<SortTh
						col="on_latest_firmware"
						label="Latest App"
						sort={sort}
						onSort={onSort}
						center
					/>
					<SortTh
						col="notes"
						label="Notes"
						sort={sort}
						onSort={onSort}
					/>
				</tr>
			</thead>
			<tbody>
				{loading && (
					<tr>
						<Td colSpan={showLive ? 11 : 10} center>
							<SkeletonGroup label="Loading SmartChargers" className="space-y-1 py-3">
								{[0, 1, 2, 3].map((n) => <Skeleton key={n} className="h-8 w-full" />)}
							</SkeletonGroup>
						</Td>
					</tr>
				)}
				{!loading && rows.length === 0 && (
					<tr>
						<Td colSpan={showLive ? 11 : 10} center>
							<EmptyState variant="area">No SmartChargers match</EmptyState>
						</Td>
					</tr>
				)}
				{!loading &&
					rows.map((r) => {
						const ext = r._ext as SmartChargerDetails | undefined;
						const save = saveExtensionField(r.id);
						const saveSpine = saveSpineField(r.id);
						const cs = chargerStatus(r);
						const live = showLive ? liveByAssetId?.[r.id] : undefined;
						return (
							<tr
								key={r.id}
								className="border-b border-gray-700/40 last:border-b-0 hover:bg-gray-700/20 transition-colors"
							>
								<Td>
									<InlineEditField
										value={
											r.current_customer_id === null ||
											r.current_customer_id === undefined
												? null
												: String(r.current_customer_id)
										}
										fieldType="select"
										options={customerOptions}
										onSave={saveCustomer(r.id)}
										renderValue={(v) => {
											// No customer FK: distinguish "internal pool, in shop" from
											// truly orphan. is_internal_pool=TRUE + no customer means Firefly
											// has it sitting in the shop — operators want to see that explicitly,
											// not a blank dash.
											if (
												v === null ||
												v === undefined ||
												v === ""
											) {
												if (r.is_internal_pool) {
													return (
														<span className="italic text-amber-300/80">
															Firefly (Internal)
														</span>
													);
												}
												return (
													<span className="italic text-on-surface-disabled">
														—
													</span>
												);
											}
											const match = customerOptions.find(
												(o) => o.value === String(v),
											);
											return match?.label ?? String(v);
										}}
									/>
								</Td>
								<Td>
									<span className="inline-flex items-center gap-1.5">
										{pinnedIds.has(r.id) && (
											<svg
												width="11"
												height="11"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
												className="text-primary flex-shrink-0"
												aria-label="Pinned"
											>
												<path d="M12 17v5" />
												<path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
											</svg>
										)}
										{wrongVpnIds.has(r.id) && (
											<svg
												width="12"
												height="12"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
												className="text-amber-400 flex-shrink-0"
												aria-label="Wrong VPN profile"
											>
												<title>Wrong VPN profile — see Inconsistencies</title>
												<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
												<path d="M12 9v4" />
												<path d="M12 17h.01" />
											</svg>
										)}
										<Link
											to={`/assets/${r.id}`}
											className="font-mono text-on-surface hover:text-primary"
										>
											{chargerShortName(r.display_code)}
										</Link>
									</span>
								</Td>
								<Td>
									<InlineEditField
										value={ext?.charger_power ?? null}
										fieldType="select"
										options={POWER_OPTIONS}
										onSave={save("charger_power")}
									/>
								</Td>
								<Td>
									<InlineEditField
										value={ext?.build_phase ?? null}
										fieldType="select"
										options={CHARGER_BUILD_PHASE_OPTIONS}
										onSave={save("build_phase")}
									/>
								</Td>
								<Td center>
									<Tooltip label="Derived from build phase, ownership, flags & assignment">
										<ChargerStatusTag status={cs} />
									</Tooltip>
								</Td>
								{showLive && (
									<Td>
										{live ? (
											<Tooltip label="Open live charger">
											<button
												type="button"
												onClick={() => onOpenLive?.(r.id)}
												className="inline-flex items-center gap-1.5 text-left hover:text-primary"
											>
												<span
													className={`w-2 h-2 rounded-full flex-shrink-0 ${
														live.online ? "bg-emerald-400" : "bg-outline"
													}`}
												/>
												<span className="text-xs text-on-surface-variant whitespace-nowrap">
													{live.online ? "Now" : (fmtLastSeen(live.last_seen) ?? "offline")}
													{(live.geo_label ?? ext?.last_seen_geo) ? ` · ${live.geo_label ?? ext?.last_seen_geo}` : ""}
												</span>
											</button>
											</Tooltip>
										) : ext?.last_seen_geo ? (
											<span className="inline-flex items-center gap-1.5 text-xs text-on-surface-variant whitespace-nowrap" title="Last known location">
												<span className="w-2 h-2 rounded-full flex-shrink-0 bg-outline" />
												{ext.last_seen_geo}
											</span>
										) : (
											<span className="text-on-surface-disabled italic text-xs">—</span>
										)}
									</Td>
								)}
								<Td>
									<OwnershipCell ownership={r.ownership} />
								</Td>
								<Td mono>
									<InlineEditField
										value={ext?.vpn_ip ?? null}
										mono
										onSave={save("vpn_ip")}
									/>
								</Td>
								<Td center>
									<InlineEditField
										value={ext?.vpn_confirmed ?? false}
										fieldType="checkbox"
										name="VPN confirmed"
										onSave={save("vpn_confirmed")}
									/>
								</Td>
								<Td center>
									<InlineEditField
										value={ext?.on_latest_firmware ?? true}
										fieldType="checkbox"
										name="On latest firmware"
										onSave={save("on_latest_firmware")}
									/>
								</Td>
								<Td>
									<NotesCell value={r.notes ?? null} onSave={saveSpine("notes")} />
								</Td>
							</tr>
						);
					})}
			</tbody>
		</table>
		</>
	);
};

// TimeMachine — columns (Seq # and Firmware hidden per SmartCharger parity):
//   Name | Build Phase | VPN | VPN✓ | Ownership | Status | Notes
//
// Module scope, per the shared table's contract 1. It matters here more than on
// a read-only list: four of these seven cells hold a LIVE editor, and a fresh
// columns identity hands flexRender a new component type, which remounts the
// open select or input mid-word. Everything a cell needs beyond its own row (the
// two save factories) arrives on `meta` and is read at render time, so none of
// these renderers may be wrapped in React.memo — that would skip the parent's
// render and read a stale meta.
//
// Sorting moved off the page's SortState onto accessorFns. Empties return
// `undefined`, never '' and never null: that is what lands them last ascending
// (first descending) instead of making the comparator inconsistent.
interface TimeMachineMeta {
	saveExtensionField: SaveFactory;
	saveSpineField: SaveFactory;
}

const tmExt = (r: AssetWithExt) => r._ext as TimeMachineDetails | undefined;

// Today's default for a VPN-carrying type, straight out of defaultSortCol — now
// the table's initial state rather than the page's.
const TIME_MACHINE_INITIAL_SORT: SortingState = [{ id: "vpn_ip", desc: false }];

const TIME_MACHINE_COLUMNS: ColumnDef<AssetWithExt, any>[] = [
	{
		id: "display_code",
		header: "Name",
		accessorFn: (r) => r.display_code,
		cell: ({ row }) => (
			<Link
				to={`/assets/${row.original.id}`}
				className="font-mono text-on-surface hover:text-primary"
			>
				{row.original.display_code}
			</Link>
		),
	},
	{
		id: "build_phase",
		header: "Build Phase",
		// The stored key, which is what the page sorted by. Both the label order
		// and the shop-floor pipeline order differ from it.
		accessorFn: (r) => tmExt(r)?.build_phase || undefined,
		cell: (info) => {
			const { saveExtensionField } = info.table.options.meta as TimeMachineMeta;
			const r = info.row.original;
			return (
				<InlineEditField
					value={tmExt(r)?.build_phase ?? null}
					fieldType="select"
					options={BUILD_PHASE_OPTIONS}
					onSave={saveExtensionField(r.id)("build_phase")}
				/>
			);
		},
	},
	{
		id: "vpn_ip",
		header: "VPN IP",
		accessorFn: (r) => vpnSortValue(tmExt(r)?.vpn_ip),
		meta: { cellClass: "font-mono" },
		cell: (info) => {
			const { saveExtensionField } = info.table.options.meta as TimeMachineMeta;
			const r = info.row.original;
			return (
				<InlineEditField
					value={tmExt(r)?.vpn_ip ?? null}
					mono
					onSave={saveExtensionField(r.id)("vpn_ip")}
				/>
			);
		},
	},
	{
		id: "vpn_confirmed",
		header: "VPN ✓",
		// A false is a VALUE, not an empty, so this one keeps 0/1 rather than
		// undefined: unconfirmed rows sort as a group, not as blanks.
		accessorFn: (r) => (tmExt(r)?.vpn_confirmed ? 1 : 0),
		meta: { align: "center" },
		cell: (info) => {
			const { saveExtensionField } = info.table.options.meta as TimeMachineMeta;
			const r = info.row.original;
			return (
				<InlineEditField
					value={tmExt(r)?.vpn_confirmed ?? false}
					fieldType="checkbox"
					name="VPN confirmed"
					onSave={saveExtensionField(r.id)("vpn_confirmed")}
				/>
			);
		},
	},
	OWNERSHIP_COLUMN,
	{
		id: "operational_status",
		header: "Status",
		// The stored key, not the label: that is what the page sorted by, and
		// the two orders differ (Out of Commission vs ooc).
		accessorFn: (r) => r.operational_status,
		meta: { align: "center" },
		cell: ({ row }) => (
			<AssetStatusTag status={row.original.operational_status} />
		),
	},
	{
		id: "notes",
		header: "Notes",
		accessorFn: (r) => r.notes || undefined,
		cell: (info) => {
			const { saveSpineField } = info.table.options.meta as TimeMachineMeta;
			const r = info.row.original;
			return (
				<InlineEditField
					value={r.notes ?? null}
					truncate
					onSave={saveSpineField(r.id)("notes")}
				/>
			);
		},
	},
];

const TimeMachineTable: React.FC<{
	rows: AssetWithExt[];
	loading: boolean;
	saveExtensionField: SaveFactory;
	saveSpineField: SaveFactory;
}> = ({ rows, loading, saveExtensionField, saveSpineField }) => (
	<DataTable<AssetWithExt>
		data={rows}
		columns={TIME_MACHINE_COLUMNS}
		getRowId={(r) => r.id}
		meta={{ saveExtensionField, saveSpineField }}
		sortable
		initialSorting={TIME_MACHINE_INITIAL_SORT}
		loading={loading}
		loadingContent={<TableLoading label="Loading assets" />}
		empty={<EmptyState variant="area">No TimeMachines match</EmptyState>}
	/>
);

// RTK / GroundStationKit / every generic type — fewer columns, mostly
// click-to-detail. Optional 'customer' column shares the SmartCharger inline-edit
// pattern: shows "Firefly (Internal)" when is_internal_pool=TRUE + no customer FK,
// customer name otherwise. Reassign-dropdown still filtered to is_customer=true
// orgs only.
//
// The column SET is a prop, so the ARRAY cannot be a module constant — but every
// column DEFINITION is, and that is what contract 1 actually needs. flexRender
// keys a cell's React element off the RENDERER's identity, so assembling a fresh
// array out of these same defs updates the cells instead of remounting them, and
// the open customer select survives. The array is memoized on the joined column
// keys as well (below), so tanstack's own column model does not rebuild either.
type SimpleColumnKey =
	| "firmware"
	| "components"
	| "customer"
	| "vpn_ip"
	| "location";

interface SimpleTableMeta {
	saveSpineField: SaveFactory;
	saveCustomer?: SaveCustomerFactory;
	customerOptions: Array<{ value: string; label: string }>;
}

// The two defaults defaultSortCol can hand a SimpleTable, as module constants so
// the prop identity is stable across renders.
const SIMPLE_SORT_BY_NAME: SortingState = [{ id: "display_code", desc: false }];
const SIMPLE_SORT_BY_VPN: SortingState = [{ id: "vpn_ip", desc: false }];

const SIMPLE_NAME_COLUMN: ColumnDef<AssetWithExt, any> = {
	id: "display_code",
	header: "Name",
	accessorFn: (r) => r.display_code,
	cell: ({ row }) => (
		<Link
			to={`/assets/${row.original.id}`}
			className="font-mono text-on-surface hover:text-primary"
		>
			{row.original.display_code}
		</Link>
	),
};

const SIMPLE_STATUS_COLUMN: ColumnDef<AssetWithExt, any> = {
	id: "operational_status",
	header: "Status",
	accessorFn: (r) => r.operational_status,
	meta: { align: "center" },
	cell: ({ row }) => (
		<AssetStatusTag status={row.original.operational_status} />
	),
};

const SIMPLE_NOTES_COLUMN: ColumnDef<AssetWithExt, any> = {
	id: "notes",
	header: "Notes",
	accessorFn: (r) => r.notes || undefined,
	cell: (info) => {
		const { saveSpineField } = info.table.options.meta as SimpleTableMeta;
		const r = info.row.original;
		return (
			<InlineEditField
				value={r.notes ?? null}
				truncate
				onSave={saveSpineField(r.id)("notes")}
			/>
		);
	},
};

// The optional five in RENDER order. The page's header row fixed this sequence
// regardless of the order the `columns` prop listed them in, and that holds.
const SIMPLE_OPTIONAL_COLUMNS: Array<
	[SimpleColumnKey, ColumnDef<AssetWithExt, any>]
> = [
	[
		"vpn_ip",
		{
			id: "vpn_ip",
			header: "VPN IP",
			accessorFn: (r) => vpnSortValue(extString(r, "vpn_ip")),
			meta: { cellClass: "text-on-surface font-mono" },
			cell: ({ row }) =>
				extString(row.original, "vpn_ip") ?? (
					<span className="text-on-surface-disabled italic">—</span>
				),
		},
	],
	[
		"firmware",
		{
			// Sorts by firmware. The page's header claimed to and did not: there
			// was no getter case for this key, so it fell through to the Name
			// default and clicking Firmware re-sorted by Name.
			id: "firmware_version",
			header: "Firmware",
			accessorFn: (r) => extString(r, "firmware_version") || undefined,
			meta: { cellClass: "text-on-surface font-mono" },
			cell: ({ row }) =>
				extString(row.original, "firmware_version") ?? (
					<span className="text-on-surface-disabled italic">—</span>
				),
		},
	],
	[
		"components",
		{
			// A count of zero is a value, not an empty: kits with no children
			// sort as zero rather than as blanks.
			id: "components",
			header: "Components",
			accessorFn: (r) => r._children_count ?? 0,
			meta: { align: "center", numeric: true, cellClass: "text-on-surface" },
			cell: ({ row }) => {
				const r = row.original;
				return (
					<div className="flex items-center justify-center gap-1.5">
						<span>{r._children_count ?? 0}</span>
						{(r._flagged_children_count ?? 0) > 0 && (
							<Tooltip label={`${r._flagged_children_count} child item${r._flagged_children_count === 1 ? '' : 's'} have active flags`}>
								<Tag hex="#fde047" size="sm">⚑ {r._flagged_children_count}</Tag>
							</Tooltip>
						)}
					</div>
				);
			},
		},
	],
	[
		"customer",
		{
			// Sorts by the customer FK, exactly as the page did. Sorting by NAME
			// would need the name decorated onto the row (an accessorFn cannot
			// read `meta`), which is a different change.
			id: "customer",
			header: "Customer",
			accessorFn: (r) => r.current_customer_id ?? undefined,
			cell: (info) => {
				const { saveCustomer, customerOptions } = info.table.options
					.meta as SimpleTableMeta;
				const r = info.row.original;
				return (
					<InlineEditField
						value={
							r.current_customer_id == null
								? null
								: String(r.current_customer_id)
						}
						fieldType="select"
						options={customerOptions}
						onSave={saveCustomer(r.id)}
						renderValue={(v) => {
							if (v === null || v === undefined || v === "") {
								if (r.is_internal_pool) {
									return (
										<span className="italic text-amber-300/80">
											Firefly (Internal)
										</span>
									);
								}
								return (
									<span className="italic text-on-surface-disabled">—</span>
								);
							}
							const match = customerOptions.find(
								(o) => o.value === String(v),
							);
							return match?.label ?? String(v);
						}}
					/>
				);
			},
		},
	],
	[
		"location",
		{
			id: "location_bucket",
			header: "Location",
			accessorFn: (r) => r.location_bucket,
			meta: { cellClass: "text-on-surface" },
			cell: ({ row }) => row.original.location_bucket,
		},
	],
];

const SimpleTable: React.FC<{
	rows: AssetWithExt[];
	loading: boolean;
	saveSpineField: SaveFactory;
	columns: SimpleColumnKey[];
	type: AssetType;
	customers?: { id: number; name: string }[];
	saveCustomer?: SaveCustomerFactory;
}> = ({
	rows,
	loading,
	saveSpineField,
	columns,
	type,
	customers,
	saveCustomer,
}) => {
	const customerOptions = useMemo(
		() => (customers ?? []).map((c) => ({ value: String(c.id), label: c.name })),
		[customers],
	);
	// One array now holds the th AND the td for each column, so a set that asks
	// for "customer" without a save handler can no longer render a header over a
	// missing cell — which is what shifted every column right of it by one.
	const wantCustomer = columns.includes("customer") && !!saveCustomer;
	const columnKey = `${columns.join("|")}|${wantCustomer}`;
	const tableColumns = useMemo(
		() => [
			SIMPLE_NAME_COLUMN,
			...SIMPLE_OPTIONAL_COLUMNS.filter(([key]) =>
				key === "customer" ? wantCustomer : columns.includes(key),
			).map(([, col]) => col),
			// After the optional columns (Customer, Location when shown) and before
			// Status: who holds it and where it is, then whose it is, then what it can do.
			OWNERSHIP_COLUMN,
			SIMPLE_STATUS_COLUMN,
			SIMPLE_NOTES_COLUMN,
		],
		// The joined keys ARE the dep. `columns` arrives as a fresh array literal
		// on every render of the page, so depending on the array itself would
		// rebuild the column model on every keystroke in the search box.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[columnKey],
	);
	return (
		<DataTable<AssetWithExt>
			data={rows}
			columns={tableColumns}
			getRowId={(r) => r.id}
			meta={{ saveSpineField, saveCustomer, customerOptions }}
			sortable
			// Today's per-type default. The page reset its own sort on a type
			// switch; here the swap between the rtk / kit / generic slots
			// remounts the table, which re-reads this. Two generic types share
			// one slot and both default to Name, so no reset is owed there.
			initialSorting={
				defaultSortCol(type.key) === "vpn_ip" && columns.includes("vpn_ip")
					? SIMPLE_SORT_BY_VPN
					: SIMPLE_SORT_BY_NAME
			}
			loading={loading}
			loadingContent={<TableLoading label="Loading assets" />}
			empty={<EmptyState variant="area">No {type.label} match</EmptyState>}
		/>
	);
};

const TableLoading: React.FC<{ label: string }> = ({ label }) => (
	<SkeletonGroup label={label} className="space-y-1 p-3">
		{[0, 1, 2, 3, 4].map((n) => <Skeleton key={n} className="h-8 w-full" />)}
	</SkeletonGroup>
);

// Sortable header cell for the SmartCharger sheet, the last table here that still
// hand-rolls one — click to toggle asc/desc, against the page's SortState.
// whitespace-nowrap prevents 2-word labels like "Build Phase" or "Needs Update" from wrapping.
const SortTh: React.FC<{
	col: string;
	label: string;
	sort: SortState;
	onSort: (col: string) => void;
	center?: boolean;
}> = ({ col, label, sort, onSort, center }) => {
	const active = sort.col === col;
	return (
		<th
			onClick={() => onSort(col)}
			className={`${center ? "text-center" : "text-left"} px-3 py-3 font-semibold cursor-pointer select-none whitespace-nowrap hover:text-on-surface transition-colors`}
		>
			{label}
			{active && (
				<span className="ml-1 text-primary">
					{sort.dir === "asc" ? "↑" : "↓"}
				</span>
			)}
		</th>
	);
};

const Td: React.FC<{
	children: React.ReactNode;
	center?: boolean;
	mono?: boolean;
	colSpan?: number;
}> = ({ children, center, mono, colSpan }) => (
	<td
		colSpan={colSpan}
		className={`${center ? "text-center" : "text-left"} px-3 py-2 text-on-surface ${mono ? "font-mono" : ""} align-top`}
	>
		{children}
	</td>
);

export default AssetsTypeList;
